/**
 * My team — a captain fills in his own team from the player app (Oct 26, 2026 — Dennis;
 * claude/Captain_Team_Link_Plan_v1.md, Slice 1).
 *
 * Opened from the "👥 Your team" button on the event's card in the player portal, or
 * straight away when his link carries ?team=1. One card per spot, captain first; a held
 * spot ("Samuelson team — player 2", no email) shows as an empty "Player 2" card; open
 * spots get "+ Add a teammate".
 *
 * The captain is never asked whether a change is a spelling fix or a swap: the server
 * decides from the email (captainTeamService.saveSpot). Nothing blocks — a card saves with
 * fields still missing. Once the organizer locks the teams, or the round has started,
 * every card is read-only.
 *
 * API: GET/PUT `${apiUrl}/player/my-team?token=&teetime_id=`
 *      PUT body { player_id | null, name, email, ghin?, tee_id? } → the team again.
 */
import React, { useEffect, useState } from "react";

const GREEN = "#2d6a4f";
const INK = "#111827";
const MUTED = "#4b5563";
const FIELD = { width: "100%", boxSizing: "border-box", border: "1px solid #9ca3af", borderRadius: 10, padding: "12px 12px", fontSize: 18, fontFamily: "inherit", color: INK, background: "#fff" };
const LABEL = { display: "block", fontSize: 16, fontWeight: 700, color: "#374151", margin: "12px 0 6px" };
const BIG_BTN = { padding: "14px 22px", fontSize: 18, fontWeight: 700, borderRadius: 12, border: "none", cursor: "pointer", fontFamily: "inherit" };

const FIELD_NAMES = { name: "name", email: "email", ghin: "GHIN #", tee: "tee" };

async function call(apiUrl, token, teetimeId, method, body) {
  const url = `${apiUrl}/player/my-team?token=${encodeURIComponent(token)}&teetime_id=${encodeURIComponent(teetimeId)}`;
  const res = await fetch(url, {
    method,
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch (_) { /* empty */ }
  if (!res.ok) {
    const e = new Error((data && data.error) || "Something went wrong. Please try again.");
    e.status = res.status;
    e.code = data && data.code;
    throw e;
  }
  return data;
}

function needsText(missing) {
  return (missing || []).map((k) => FIELD_NAMES[k] || k).join(", ");
}

function Badge({ complete, missing }) {
  if (complete) {
    return <span style={{ fontSize: 15, fontWeight: 800, color: "#166534", background: "#dcfce7", border: "1px solid #86efac", borderRadius: 999, padding: "4px 12px", whiteSpace: "nowrap" }}>Complete ✓</span>;
  }
  return <span style={{ fontSize: 15, fontWeight: 800, color: "#92400e", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 999, padding: "4px 12px" }}>Needs: {needsText(missing)}</span>;
}

/** The form for one spot (a member, a held spot, or a new teammate). */
function SpotForm({ member, team, busy, error, onSave, onCancel }) {
  const required = team.required || [];
  const [name, setName] = useState(member ? member.name : "");
  const [email, setEmail] = useState(member ? member.email : "");
  const [ghin, setGhin] = useState(member ? member.ghin : "");
  const [teeId, setTeeId] = useState(member ? member.tee_id : "");
  const showTee = Array.isArray(team.tees) && team.tees.length > 0;
  // GHIN # only when the organizer ticks it under "Each golfer needs:" (Oct 28, 2026 — Dennis:
  // a gross event shouldn't ask for it). Same rule as Tee.
  const showGhin = required.includes("ghin");
  const star = (k) => (required.includes(k) ? " *" : "");
  const submit = (e) => {
    e.preventDefault();
    const body = { player_id: member ? member.player_id : null, name: name.trim(), email: email.trim() };
    if (showGhin && (ghin.trim() || (member && member.ghin))) body.ghin = ghin.trim();
    if (showTee) body.tee_id = teeId || "";
    onSave(body);
  };
  return (
    <form onSubmit={submit} noValidate style={{ marginTop: 6 }}>
      <label style={LABEL} htmlFor="mt-name">Name *</label>
      <input id="mt-name" style={FIELD} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" autoCapitalize="words" placeholder="First and last name" />
      <label style={LABEL} htmlFor="mt-email">Email{star("email")}</label>
      <input id="mt-email" style={FIELD} type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" autoCapitalize="none" placeholder="name@example.com" />
      {showGhin && (
        <>
          <label style={LABEL} htmlFor="mt-ghin">GHIN # *</label>
          <input id="mt-ghin" style={FIELD} inputMode="numeric" value={ghin} onChange={(e) => setGhin(e.target.value)} autoComplete="off" placeholder="e.g. 1234567" />
        </>
      )}
      {showTee && (
        <>
          <label style={LABEL} htmlFor="mt-tee">Tee{star("tee")}</label>
          <select id="mt-tee" style={FIELD} value={teeId} onChange={(e) => setTeeId(e.target.value)}>
            <option value="">Choose a tee…</option>
            {team.tees.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </>
      )}
      {error && <div role="alert" style={{ marginTop: 12, fontSize: 17, color: "#b42318", fontWeight: 600, lineHeight: 1.4 }}>{error}</div>}
      <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
        <button type="submit" disabled={busy} style={{ ...BIG_BTN, background: busy ? "#9ca3af" : GREEN, color: "#fff", flex: "1 1 160px" }}>{busy ? "Saving…" : "Save"}</button>
        <button type="button" onClick={onCancel} disabled={busy} style={{ ...BIG_BTN, background: "#fff", color: "#374151", border: "1px solid #9ca3af", flex: "1 1 120px" }}>Cancel</button>
      </div>
    </form>
  );
}

export default function MyTeam({ apiUrl, token, teetimeId, onClose, onChanged }) {
  const [team, setTeam] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState(null);   // player_id, or "new"
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  useEffect(() => {
    let off = false;
    call(apiUrl, token, teetimeId, "GET")
      .then((d) => { if (!off) setTeam(d); })
      .catch((e) => { if (!off) setLoadError(e.message); });
    return () => { off = true; };
  }, [apiUrl, token, teetimeId]);

  // Escape closes, and the page behind doesn't scroll while the window is open.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [busy, onClose]);

  const readOnly = !!team && (team.locked || team.started);

  const save = async (body) => {
    setBusy(true); setError(""); setSaved("");
    try {
      const d = await call(apiUrl, token, teetimeId, "PUT", body);
      setTeam(d);
      setEditing(null);
      setSaved(`Saved — ${body.name}.`);
      if (onChanged) onChanged(d);
    } catch (e) {
      setError(e.message);
      // Locked or started while the window was open: show the read-only notice.
      if (e.code === "LOCKED" || e.code === "STARTED") {
        setTeam((t) => (t ? { ...t, locked: e.code === "LOCKED" || t.locked, started: e.code === "STARTED" || t.started } : t));
        setEditing(null);
      }
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (id) => { setEditing(id); setError(""); setSaved(""); };

  const title = (m, i) => {
    if (m.held) {
      const match = /player\s*(\d+)\s*$/i.exec(m.placeholder_name || "");
      return `Player ${match ? match[1] : i + 1}`;
    }
    return m.name;
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="My team" data-testid="my-team"
      style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(17,24,39,0.55)", display: "flex", justifyContent: "center", alignItems: "stretch" }}>
      <div style={{ background: "#f9fafb", width: "100%", maxWidth: 640, overflowY: "auto", WebkitOverflowScrolling: "touch", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }}>
        <div style={{ position: "sticky", top: 0, zIndex: 1, background: GREEN, color: "#fff", padding: "16px 16px 14px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>👥 {team ? team.team_name : "My team"}</div>
            {team && <div style={{ fontSize: 16, opacity: 0.92, marginTop: 2 }}>{[team.event_name, team.date_text].filter(Boolean).join(" · ")}</div>}
          </div>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close"
            style={{ ...BIG_BTN, padding: "10px 16px", background: "rgba(255,255,255,0.18)", color: "#fff", border: "1px solid rgba(255,255,255,0.5)" }}>Close</button>
        </div>

        <div style={{ padding: "16px 16px 40px" }}>
          {loadError && <div role="alert" style={{ fontSize: 18, color: "#b42318", fontWeight: 600 }}>{loadError}</div>}
          {!team && !loadError && <div style={{ fontSize: 18, color: MUTED }}>Loading your team…</div>}

          {team && (
            <>
              <div style={{ fontSize: 18, color: INK, lineHeight: 1.45, marginBottom: 6 }}>
                <strong>{team.complete_count} of {team.size}</strong> complete.
              </div>
              {readOnly ? (
                <div data-testid="my-team-locked" style={{ fontSize: 18, lineHeight: 1.45, color: "#7a2e0e", background: "#fff7ed", border: "1px solid #fdba74", borderRadius: 12, padding: "12px 14px", margin: "8px 0 14px" }}>
                  🔒 {team.locked ? "Teams are locked." : "The round has started."} Ask the organizer to make changes.
                </div>
              ) : (
                <div style={{ fontSize: 17, lineHeight: 1.5, color: MUTED, margin: "4px 0 14px" }}>
                  Tap <strong>Edit</strong> to fill in a teammate. Swapping a player? Type the new golfer's name and email over the old ones and save.
                </div>
              )}
              {saved && <div role="status" style={{ fontSize: 17, color: "#166534", fontWeight: 700, margin: "0 0 12px" }}>✓ {saved}</div>}

              {team.members.map((m, i) => {
                const isEditing = editing === m.player_id;
                return (
                  <div key={m.player_id} data-testid="my-team-card"
                    style={{ background: "#fff", border: `2px solid ${isEditing ? GREEN : "#e5e7eb"}`, borderRadius: 14, padding: "14px 14px 16px", marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: m.held ? MUTED : INK, wordBreak: "break-word" }}>
                          {title(m, i)}{m.captain ? <span style={{ fontSize: 15, fontWeight: 700, color: GREEN }}> · Captain (you)</span> : null}
                        </div>
                        {!isEditing && (
                          <div style={{ fontSize: 17, color: MUTED, marginTop: 4, lineHeight: 1.45, wordBreak: "break-word" }}>
                            {m.held ? "Not named yet" : (m.email || "No email yet")}
                            {m.ghin ? <div>GHIN # {m.ghin}</div> : null}
                            {m.tee_name ? <div>{m.tee_name} tees</div> : null}
                          </div>
                        )}
                      </div>
                      <Badge complete={m.complete} missing={m.missing} />
                    </div>
                    {isEditing ? (
                      <SpotForm member={m} team={team} busy={busy} error={error} onSave={save} onCancel={() => { setEditing(null); setError(""); }} />
                    ) : (!readOnly && (
                      <button type="button" onClick={() => startEdit(m.player_id)} disabled={busy || editing !== null}
                        style={{ ...BIG_BTN, marginTop: 12, width: "100%", background: "#fff", color: GREEN, border: `2px solid ${GREEN}`, opacity: editing !== null ? 0.5 : 1 }}>
                        {m.held ? "Fill in" : "Edit"}
                      </button>
                    ))}
                  </div>
                );
              })}

              {!readOnly && team.open_spots > 0 && (
                editing === "new" ? (
                  <div style={{ background: "#fff", border: `2px solid ${GREEN}`, borderRadius: 14, padding: "14px 14px 16px", marginBottom: 12 }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: INK }}>New teammate</div>
                    <SpotForm member={null} team={team} busy={busy} error={error} onSave={save} onCancel={() => { setEditing(null); setError(""); }} />
                  </div>
                ) : (
                  <button type="button" data-testid="my-team-add" onClick={() => startEdit("new")} disabled={busy || editing !== null}
                    style={{ ...BIG_BTN, width: "100%", background: "#fff", color: GREEN, border: `2px dashed ${GREEN}`, opacity: editing !== null ? 0.5 : 1 }}>
                    + Add a teammate ({team.open_spots} open spot{team.open_spots === 1 ? "" : "s"})
                  </button>
                )
              )}

              <div style={{ fontSize: 15, color: MUTED, lineHeight: 1.5, marginTop: 18 }}>
                * required for this event. Saving doesn't email anyone — the organizer sends the event emails.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
